import React, { FunctionComponent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ConsentContext } from '@context/ConsentContext'
import { PostalCode, PostalCodeEntry } from '@models/PostalCode'
import { PostalCodeProps } from '../AnswerProps'
import { useTranslation } from 'react-i18next'
import API from '@api/client'
import Button from '@components/Button/Button'

import FormField from '@components/FormField/FormField'
import ModalDialog, { handleInfoUrl } from '@components/ModalDialog/ModalDialog'
import Markdown from '@components/Markdown/Markdown'
import { useNavigate } from 'react-router-dom'
import './PostalCodeInput.scss'

import capitalize from 'lodash/capitalize'
import debounce from 'lodash/debounce'

const _sort = (a: Partial<PostalCode>, b: Partial<PostalCode>) => {
  const aNmCirc = a?.nmCirc as string
  const bNmCirc = b?.nmCirc as string
  if (aNmCirc < bNmCirc) {
    return -1
  }
  if (aNmCirc > bNmCirc) {
    return 1
  }
  return 0
}

const _groupResults = (input: PostalCode[]) => {
  return (input || []).reduce((acc, { nmCirc, code }) => {
    if (!nmCirc) {
      return acc
    }
    acc[nmCirc] = acc[nmCirc] ?? { code, nmCirc }
    return acc
  }, {} as Record<string, Partial<PostalCode>>)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _randomize = (input: any[]): Partial<PostalCode[]> => {
  let currentIndex = input.length,
    randomIndex

  while (currentIndex != 0) {
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex--
    ;[input[currentIndex], input[randomIndex]] = [input[randomIndex], input[currentIndex]]
  }

  return input
}

export const PostalCodeInput: FunctionComponent<PostalCodeProps> = ({
  question,
  onSelection,
  answer,
}: PostalCodeProps) => {
  const { t, i18n } = useTranslation('Common')

  const [randomIndex, setRandomIndex] = useState(answer ? 0 : -1)
  const [choices, setChoices] = useState((answer ? [answer] : []) as Partial<PostalCode[]>)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedChoice, setSelectedChoice] = useState(answer as any)
  const { hasAutoFill } = useContext(ConsentContext)
  const [infoDialogVisible, setInfoDialogVisible] = useState(false)
  const navigate = useNavigate()
  const onSelectionRef = useRef(onSelection)
  onSelectionRef.current = onSelection

  const debounceCallback = useCallback(
    debounce(async (value: string) => {
      if (typeof value === 'undefined') {
        return
      }
      try {
        setSelectedChoice(undefined)
        setRandomIndex(-1)
        const cleanValue = value.replace(/_/g, '').replace(/\s/g, '')
        if (!cleanValue.trim()) {
          setChoices([])
          return
        }
        const response = await API.searchCircumscriptions(cleanValue)
        const results = _randomize(
          Object.values(_groupResults(response?.data ?? []))
            .slice(0, 6)
            .sort(_sort),
        ) as Partial<PostalCode>[]
        setChoices(results)
        if (!results.length) {
          return
        }
        const randomChoiceIndex = Math.floor(Math.random() * results.length)
        setRandomIndex(randomChoiceIndex)
        onSelectionRef.current(results[randomChoiceIndex] as PostalCodeEntry)
      } catch {
        setChoices([])
        setRandomIndex(-1)
      }
    }, 1000),
    [],
  )

  const textHandler = (value: string) => {
    if (value === '' || !value) {
      setSelectedChoice(undefined)
      setChoices([])
      setRandomIndex(-1)
      return
    }
    debounceCallback(value?.trim())
  }

  const handleChoiceSelect = (value: Partial<PostalCode> | undefined) => {
    setSelectedChoice(value)
    setRandomIndex(-1)
    onSelection(value as PostalCodeEntry)
  }

  const randomClassName = (idx: number) => (idx === randomIndex ? 'random' : '')
  const selectedClassName = (item: PostalCode | undefined) => (selectedChoice?.code === item?.code ? 'selected' : '')

  useEffect(() => {
    if (hasAutoFill) return
    if (question.optional && !selectedChoice) {
      textHandler('  ')
    }
  }, [])

  const closeInfoModal = () => {
    setInfoDialogVisible(false)
  }
  const renderInfoDialogBody = (text: string) => <Markdown content={text} />

  const { language: currentLanguage } = i18n
  const { meta } = question
  const infoText = meta?.[`info${capitalize(currentLanguage)}` as keyof typeof meta] as string | undefined

  const handleInfoModalAction = useCallback(() => {
    closeInfoModal()
    handleInfoUrl(meta?.infoUrl, navigate)
  }, [meta?.infoUrl])
  return (
    <div className="PostalCodeInput">
      {infoText && (
        <ModalDialog
          bodyComponent={renderInfoDialogBody(infoText)}
          onClose={closeInfoModal}
          visible={infoDialogVisible}
          actionText={t('action.more')}
          action={handleInfoModalAction}
        />
      )}
      <FormField
        placeholder={question.hint}
        onChange={textHandler}
        value={(answer as Partial<PostalCode>)?.code}
        mask={question.meta?.inputMask}
      />

      <div className="results">
        <div className="info">
          <p>
            {t('circonscription')} {!selectedChoice && t('random')}
          </p>
        </div>
        <div className="list">
          {choices.map((item, idx) => (
            <Button
              onClick={() => handleChoiceSelect(item)}
              key={item?.nmCirc ?? idx}
              propClassName={`${randomClassName(idx)} ${selectedClassName(item)}`}>
              {item?.nmCirc}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PostalCodeInput
